import type { AgentTool, AgentToolResult } from "@elyracode/agent-core";
import type { ImageContent } from "@elyracode/ai";
import { formatDimensionNote, resizeImage } from "../../utils/image-resize.js";
import type { ExtensionContext, ToolDefinition } from "../extensions/types.js";

/**
 * Hard provider limit safety net for images in extension tool results.
 *
 * Providers reject oversized images outright (Anthropic: any dimension over
 * 8000px, ~5MB payload), which fails the whole turn with a 400 — e.g. a
 * full-page screenshot_url capture of a long page. The read tool already
 * guards its own images; extension tools bypassed that path. This cap is
 * deliberately looser than the user-facing autoResize preference (2000px):
 * it only kicks in when an image would otherwise break the request.
 */
const PROVIDER_MAX_IMAGE_DIMENSION = 7500;

async function enforceProviderImageLimits<TDetails>(
	result: AgentToolResult<TDetails>,
): Promise<AgentToolResult<TDetails>> {
	const content = result?.content;
	if (!Array.isArray(content) || !content.some((c) => c?.type === "image")) {
		return result;
	}

	const sanitized: typeof content = [];
	let changed = false;

	for (const item of content) {
		if (item?.type !== "image" || typeof (item as ImageContent).data !== "string") {
			sanitized.push(item);
			continue;
		}
		try {
			const resized = await resizeImage(item as ImageContent, {
				maxWidth: PROVIDER_MAX_IMAGE_DIMENSION,
				maxHeight: PROVIDER_MAX_IMAGE_DIMENSION,
			});
			if (!resized?.wasResized) {
				// Within limits, or resizing unavailable — keep the original.
				sanitized.push(item);
				continue;
			}
			changed = true;
			sanitized.push({ ...item, data: resized.data, mimeType: resized.mimeType });
			const note = formatDimensionNote(resized);
			if (note) {
				sanitized.push({ type: "text", text: note });
			}
		} catch {
			sanitized.push(item);
		}
	}

	return changed ? { ...result, content: sanitized } : result;
}

/** Wrap a ToolDefinition into an AgentTool for the core runtime. */
export function wrapToolDefinition<TDetails = unknown>(
	definition: ToolDefinition<any, TDetails>,
	ctxFactory?: () => ExtensionContext,
): AgentTool<any, TDetails> {
	return {
		name: definition.name,
		label: definition.label,
		description: definition.description,
		parameters: definition.parameters,
		prepareArguments: definition.prepareArguments,
		executionMode: definition.executionMode,
		execute: async (toolCallId, params, signal, onUpdate) => {
			const result = await definition.execute(
				toolCallId,
				params,
				signal,
				onUpdate,
				ctxFactory?.() as ExtensionContext,
			);
			return enforceProviderImageLimits(result);
		},
	};
}

/** Wrap multiple ToolDefinitions into AgentTools for the core runtime. */
export function wrapToolDefinitions(
	definitions: ToolDefinition<any, any>[],
	ctxFactory?: () => ExtensionContext,
): AgentTool<any>[] {
	return definitions.map((definition) => wrapToolDefinition(definition, ctxFactory));
}

/**
 * Synthesize a minimal ToolDefinition from an AgentTool.
 *
 * This keeps AgentSession's internal registry definition-first even when a caller
 * provides plain AgentTool overrides that do not include prompt metadata or renderers.
 */
export function createToolDefinitionFromAgentTool(tool: AgentTool<any>): ToolDefinition<any, unknown> {
	return {
		name: tool.name,
		label: tool.label,
		description: tool.description,
		parameters: tool.parameters as any,
		prepareArguments: tool.prepareArguments,
		executionMode: tool.executionMode,
		execute: async (toolCallId, params, signal, onUpdate) => tool.execute(toolCallId, params, signal, onUpdate),
	};
}
